import AddIcon from "@mui/icons-material/AddRounded";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Button, Stack, Tab, Typography } from "@mui/material";
import type { PropsWithChildren } from "react";
import { useState } from "react";
import BankAccountCardsView from "./BankAccountCardsView";
import ChangeRequestsStatus from "./ChangeRequestsStatus";
import FundAssignmentsView from "./FundAssignmentsView";
import useBankAccountsContext from "./useBankAccountsContext";

interface Props {
  onAddBankAccount: () => void;
  onChangeRequestCanceled: (workflowId: string) => void;
}

type TabValue = "fund_assignments" | "bank_accounts";

const Panel = ({ children, value }: PropsWithChildren<{ value: TabValue }>) => (
  <TabPanel value={value} sx={{ p: 0, flex: 1, minHeight: 0, overflowY: "auto" }}>
    {children}
  </TabPanel>
);

const BankAccountsView = ({ onAddBankAccount, onChangeRequestCanceled }: Props) => {
  const { canUserEdit, availableFundInvestorIds } = useBankAccountsContext();

  const [selectedTab, setSelectedTab] = useState<TabValue>("fund_assignments");

  return (
    <Stack spacing={2} sx={{ width: "100%", flex: 1, minHeight: 0 }}>
      <Stack spacing={2}>
        <ChangeRequestsStatus onChangeRequestCanceled={onChangeRequestCanceled} />

        {canUserEdit && (
          <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
            <Typography color="textSecondary">
              These bank accounts will be used to receive distributions from your fund investments. You have the option
              to assign different bank accounts for each fund.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onAddBankAccount}
              sx={{ minWidth: "fit-content" }}
              disabled={availableFundInvestorIds.length === 0}
            >
              Add Bank Account
            </Button>
          </Stack>
        )}

        {!canUserEdit && (
          <Typography color="textSecondary">
            These bank accounts will be used to receive distributions from your fund investments.
          </Typography>
        )}
      </Stack>
      <TabContext value={selectedTab}>
        <TabList onChange={(_, value) => setSelectedTab(value as TabValue)}>
          <Tab value="fund_assignments" label="Fund Assignments" />
          <Tab value="bank_accounts" label="Bank Accounts" />
        </TabList>
        <Panel value="fund_assignments">
          <FundAssignmentsView />
        </Panel>
        <Panel value="bank_accounts">
          <BankAccountCardsView />
        </Panel>
      </TabContext>
    </Stack>
  );
};

export default BankAccountsView;
